import { format, startOfDay, subDays } from 'date-fns';
import type { SlcpDateRange } from '../-types';

/**
 * The endpoint's querystring schema is `format: 'date-time'` on both bounds and
 * rejects anything else, so every value written to the URL is a full ISO string
 * pinned to the start of the chosen day — the same normalisation the old
 * dashboard did on input.
 */
export function toBoundary(date: Date): string {
  return startOfDay(date).toISOString();
}

/**
 * A preset such as "last 30 days": that many days back, through today. Both
 * bounds are always set, unlike `SlcpDateRange` where either can be absent.
 */
export function quickRange(days: number): { from: string; to: string } {
  const now = new Date();
  return { from: toBoundary(subDays(now, days)), to: toBoundary(now) };
}

/** Drops empty bounds rather than sending `from=` — the schema forbids it. */
export function toQueryString({ from, to }: SlcpDateRange): string {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  return params.toString();
}

/**
 * A bound as a Date, or null when it is absent or unparseable — a hand-edited
 * URL is the realistic source of one that doesn't parse.
 */
export function parseBoundary(value: string | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Human label for the active range. Either bound can stand alone, so all four
 * cases are spelled out rather than rendered as a dash with a gap on one side.
 */
export function formatRangeLabel(
  range: SlcpDateRange,
  t: (key: string, params?: Record<string, string | number>) => string,
): string {
  const from = parseBoundary(range.from);
  const to = parseBoundary(range.to);

  if (from && to) {
    return t('filter.between', {
      start: format(from, 'MMM dd'),
      end: format(to, 'MMM dd, yyyy'),
    });
  }
  if (from) {
    return t('filter.fromOnly', { date: format(from, 'MMM dd, yyyy') });
  }
  if (to) {
    return t('filter.untilOnly', { date: format(to, 'MMM dd, yyyy') });
  }
  return t('filter.allTime');
}
